import JustValidate from 'just-validate';
export class CommonForm extends HTMLElement {
  constructor() {
    super();
    this._form = null;
  }
  connectedCallback() {
    // Get the form element
    this._form = this.querySelector('form');
    if (!this._form) {
      console.error('No form element found in the component');
      return;
    }

    // Attach the validator
    this._validator = this.attachValidator();

    // Set up conditional field listeners
    this.setupConditionalFieldListeners();

    // Add a submit handler to prevent submission when invalid
    this._form.addEventListener('submit', this.handleSubmit.bind(this));
  }
  disconnectedCallback() {
    if (this._form) {
      this._form.removeEventListener('submit', this.handleSubmit.bind(this));
    }
    this?._validator?.destroy?.();
  }
  handleSubmit(event) {
    // Check for honeypot field
    const potField = this._form.querySelector('input[name="pot"]');
    if (potField && potField.value.trim() !== '') {
      // Honeypot field is filled - bot detected
      event.preventDefault();
      // Redirect to homepage
      window.location.href = '/';
      return;
    }

    // Validate the form before submitting
    if (this._validator) {
      // Prevent the form from submitting immediately
      event.preventDefault();

      // Validate the form
      this._validator.validate().then(isValid => {
        // If valid, submit the form programmatically
        if (isValid) {
          // If child classes haven't already handled the submission
          if (!event.defaultPrevented) {
            this._form.submit();
          }
        }
        // If not valid, the form submission remains prevented
      });
    }
  }
  attachValidator() {
    // Get messages from form data attributes if available
    const requiredMsg = this._form.getAttribute('data-msg-required') || 'This field is required';
    const emailMsg = this._form.getAttribute('data-msg-mail') || 'Please enter a valid email address';
    const identicalMsg = this._form.getAttribute('data-msg-identical') || 'Fields must match';
    const minLengthMsg = this._form.getAttribute('data-msg-min-length') || 'Field must be at least %s characters';
    const passwordMsg = this._form.getAttribute('data-msg-password') || 'Password must contain at least one letter, one number, and one special character';

    // Initialize validator with empty style for error labels
    const validator = new JustValidate(this._form, {
      errorFieldCssClass: 'is-invalid',
      successFieldCssClass: 'is-valid',
      errorLabelStyle: {},
      // Empty style object to override default inline styles
      successLabelStyle: {} // Empty style object for success messages too
    });

    // Get all form inputs, selects, and textareas
    const formElements = this._form.querySelectorAll('input, select, textarea');

    // Loop through each form element and add appropriate validators
    formElements.forEach(element => {
      if (!element.name) return; // Skip elements without a name

      // Skip hidden fields and submit buttons
      if (element.type === 'hidden' || element.type === 'submit') return;
      const fieldRules = [];
      const fieldName = element.name;

      // Add required validation for required fields (but not if they have conditional validation)
      if (element.required && !element.hasAttribute('data-required-if')) {
        fieldRules.push({
          rule: 'required',
          errorMessage: requiredMsg
        });
      }

      // Add email validation for email fields
      if (element.type === 'email') {
        fieldRules.push({
          rule: 'email',
          errorMessage: emailMsg
        });
      }

      // Check for minimum length requirement
      if (element.hasAttribute('data-min-length')) {
        const minLength = parseInt(element.getAttribute('data-min-length'), 10);
        if (!isNaN(minLength)) {
          fieldRules.push({
            rule: 'minLength',
            value: minLength,
            errorMessage: minLengthMsg.replace('%s', minLength.toString())
          });
        }
      }

      // Password complexity check using a single regex
      if (element.getAttribute('data-password-pattern') === 'true') {
        fieldRules.push({
          validator: value => {
            // Single regex that checks for at least one letter, one number, and one special character
            const passwordRegex = /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[\W_]).+$/;
            return passwordRegex.test(value);
          },
          errorMessage: passwordMsg
        });
      }

      // Add identical validation for fields that need to match another field
      if (element.hasAttribute('data-identical')) {
        const targetFieldName = element.getAttribute('data-identical');
        fieldRules.push({
          validator: value => {
            const targetField = this._form.querySelector(`[name="${targetFieldName}"]`);
            return targetField ? value === targetField.value : true;
          },
          errorMessage: identicalMsg
        });
      }

      // Add conditional required validation for fields that depend on other fields
      if (element.hasAttribute('data-required-if')) {
        const conditionData = element.getAttribute('data-required-if');
        try {
          const conditions = JSON.parse(conditionData);
          fieldRules.push({
            validator: value => {
              // Check if any of the conditions are met
              const isConditionMet = conditions.some(condition => {
                return Object.entries(condition).every(([conditionFieldName, expectedValue]) => {
                  const targetField = this._form.querySelector(`[name="${conditionFieldName}"]`);
                  return targetField && targetField.value === expectedValue;
                });
              });

              // If condition is met, field is required (value must not be empty)
              // If condition is not met, field is not required (always valid)
              if (!isConditionMet) {
                return true; // Field is not required, always valid
              }

              // Field is required, check if it has a value
              const isValid = value !== null && value !== undefined && value.toString().trim() !== '';
              return isValid;
            },
            errorMessage: requiredMsg
          });
        } catch (e) {
          console.warn(`Invalid data-required-if format for field ${fieldName}:`, conditionData);
        }
      }

      // Only add field to validator if it has rules
      if (fieldRules.length > 0) {
        validator.addField(`[name="${fieldName}"]`, fieldRules, {
          errorsContainer: this.querySelector(`[data-error="${fieldName}"]`) || null
        });
      }
    });
    return validator;
  }
  setupConditionalFieldListeners() {
    // Get all fields with data-required-if attribute
    const conditionalFields = this._form.querySelectorAll('[data-required-if]');
    conditionalFields.forEach(conditionalField => {
      const conditionData = conditionalField.getAttribute('data-required-if');
      try {
        const conditions = JSON.parse(conditionData);

        // Extract all field names that this conditional field depends on
        const dependentFieldNames = new Set();
        conditions.forEach(condition => {
          Object.keys(condition).forEach(fieldName => {
            dependentFieldNames.add(fieldName);
          });
        });

        // Add change listeners to all dependent fields
        dependentFieldNames.forEach(fieldName => {
          const dependentField = this._form.querySelector(`[name="${fieldName}"]`);
          if (dependentField) {
            dependentField.addEventListener('change', () => {
              this.updateConditionalFieldState(conditionalField, conditions);
            });

            // Also trigger on initial load to set correct state
            this.updateConditionalFieldState(conditionalField, conditions);
          }
        });
      } catch (e) {
        console.warn(`Invalid data-required-if format for field ${conditionalField.name}:`, conditionData);
      }
    });
  }
  updateConditionalFieldState(conditionalField, conditions) {
    // Check if any of the conditions are met
    const isConditionMet = conditions.some(condition => {
      return Object.entries(condition).every(([fieldName, expectedValue]) => {
        const targetField = this._form.querySelector(`[name="${fieldName}"]`);
        return targetField && targetField.value === expectedValue;
      });
    });

    // Toggle data-req attribute based on condition
    if (isConditionMet) {
      conditionalField.setAttribute('data-req', 'true');
    } else {
      conditionalField.removeAttribute('data-req');
    }
  }
}
customElements.define('common-form', CommonForm);